<template>
  <div class="w-full">
    <b-row>
      <b-modal id="modal-assign" hide-footer ref="assignSubmissions" title="Assign Submission">
        <Assignment :id="taskId" type="cf" permission="can_view_continuous_filing_tasks" />
      </b-modal>
      <b-modal ref="addPRN" title="Change PRN" size="md" hide-footer>
        <AddPRN :tracking-number="application.trackingNo" />
      </b-modal>
      <b-modal ref="exemptionModal" title="Stamp Duty Exemption" size="lg" hide-footer>
        <StampDutyExemptionForm 
          :tracking-number="selectedApplication.trackingNo" 
          :application-types="continuousFilingApplicationTypes"
          @exemption-submitted="handleExemptionSubmitted" />
      </b-modal>
      <b-modal ref="rollbackModal" title="Rollback Approved Application" size="md" hide-footer>
        <b-col class="mx-auto">
          <form @submit.prevent="submitRollback">
            <div class="form-group">
              <label class="text-xs font-bold text-gray">Tracking No.</label>
              <b-form-input class="text-sm my-1 font-bold" :disabled="true" v-model="rollbackForm.trackingNo"/>
            </div>
            <div class="form-group mt-2">
              <label class="text-xs font-bold text-gray">Rationale</label>
              <b-form-textarea
                class="text-sm my-1"
                placeholder="Enter rationale..."
                v-model="rollbackForm.rationale"
                rows="4"
                required />
            </div>
            <div class="form-group mt-3">
              <b-form-checkbox v-model="rollbackForm.keepDocuments" class="rollback-keep-docs">
                <span class="ml-2">Keep documents</span>
              </b-form-checkbox>
            </div>
            <button class="mt-3 w-full bg-red px-2 py-1 rounded-sm text-white" type="submit" :disabled="loadingRollback">
              Rollback <b-spinner v-if="loadingRollback" small></b-spinner>
            </button>
          </form>
        </b-col>
      </b-modal>
    </b-row>

    <b-row class="my-2 mx-1">
      <b-col cols="3">
        <b-form-group label=" Search" label-for="date-input" class="mb-0">
        <b-form-input
          class="text-gray text-sm noFocus"
          style="padding:6px 5px;"
          v-model="search"
          type="text"
          placeholder="Search by Tracking No."
          required
          v-on:keyup.enter="fetch">
        </b-form-input>
        </b-form-group>
      </b-col>
      <b-col cols="2">
        <b-form-group label="Filter" label-for="date-input" class="mb-0">
          <b-form-select
              v-model="statuses"
              v-on:change="fetch"
              class="form-select noFocus text-gray text-sm">
            <b-select-option :value="null">--Filter by Status--</b-select-option>
            <b-select-option value="DRAFT">Draft</b-select-option>
            <b-select-option value="PENDING">In Queue</b-select-option>
            <b-select-option value="ASSIGNED">Assigned</b-select-option>
            <b-select-option value="QUERIED">Queried</b-select-option>
            <b-select-option value="APPROVED">Approved</b-select-option>
            <b-select-option value="ESCALATED">Escalated</b-select-option>
              <b-select-option value="AWAITING_PAYMENT">Confirming payment</b-select-option>
              <b-select-option value="PAYMENT_FAILED">Payment failed</b-select-option>
              <b-select-option value="AWAITING_GAZETTE_FILING">Awaiting Gazette filing</b-select-option>
              <b-select-option value="QUERIED_FOR_PAYMENT">Queried for Payment</b-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="2">
        <b-form-group label="Date registered from" label-for="date-input" class="mb-0">
          <b-form-input
            class="text-gray text-sm noFocus"
            style="margin-top: 1px; padding:6px 5px;"
            v-model="startRegistrationDate"
            type="date"
            required
            v-on:change="fetch">
          </b-form-input>
        </b-form-group>
      </b-col>
      <b-col cols="2">
        <b-form-group label="Date registered to" label-for="date-input" class="mb-0">
          <b-form-input
            class="text-gray text-sm noFocus"
            style="margin-top: 1px; padding:6px 5px;"
            v-model="endRegistrationDate"
            type="date"
            required
            v-on:change="fetch">
          </b-form-input>
        </b-form-group>
      </b-col>
      <b-col cols="1">
        <b-form-select v-model="limit" class="w-full text-xs form-select border-0 paginate-details" v-on:change="fetch">
          <b-select-option value="12">12</b-select-option>
          <b-select-option value="20">20</b-select-option>
          <b-select-option value="50">50</b-select-option>
          <b-select-option value="100">100</b-select-option>
        </b-form-select>
      </b-col>
      <b-col cols="2">
        <b-row>
          <b-col class="mx-auto">
            <p class="paginate-details text-center text-xs font-bold">
              <span>{{ ((currentPage - 1) * limit) + 1 }}</span> -
              <span v-if="limit > submissions.length ">{{ pagination.totalElements }}</span>
              <span v-else>{{ currentPage * limit }}</span>
              of {{ pagination.totalElements }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <div class="mx-auto">
      <b-progress value="100" v-if="loading" variant="#007ac5" height="0.2rem" animated></b-progress>
    </div>

    <b-row>
      <b-col cols="12">
        <table class="min-w-full leading-normal">
          <thead>
          <tr class="border-b border-t border-grey">
            <th class="t-header">#</th>
            <th class="t-header">Tracking No.</th>
            <th class="t-header">Company Name</th>
            <th class="t-header">Type</th>
            <th class="t-header text-center">Meeting Date</th>
            <th class="t-header text-center">Status</th>
            <th class="t-header text-center">Assignment</th>
            <th class="t-header text-center">Action</th>
            <th class="t-header text-center">Submission/Approval Date</th>
          </tr>
          </thead>

          <tbody>
          <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(submission, idx) in submissions" :key="idx">
            <td class="t-body">{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td class="t-body">
                <span @click="showDetails(submission.trackingNo)"
                      class="text-blue font-bold cursor-pointer hover:underline">
                  {{submission.parentTrackingNo ? submission.parentTrackingNo : submission.trackingNo}}
                </span>
            </td>
            <td class="t-body">
              <p class="font-bold">{{ submission.companyName ? submission.companyName : submission.name }}</p>
              <p class="text-blue italic">{{ submission.brn }}</p>
            </td>
            <td class="t-body text-xs">
              <span v-if="submission.resolutionType === 'MULTI_FILING'">
                <p v-for="(filing, idx) in submission.filingChangeType" :key="idx">
                      <span v-if="filing && filing.includes('APPEND')">
                        - RESOLUTION
                      </span>
                      <span v-else-if="filing">
                        - {{ filing.replaceAll("_", " ") }}
                      </span>
                    </p>
              </span>
                <span v-else>
                    - {{submission.resolutionType}}
                </span>
            </td>
            <td class="t-body text-center">
              {{ dateFormat(submission.dateOfMeeting) }}
            </td>

            <td class="t-body text-center">
              <p class="text-xs px-2 py-1 text-capitalize text-center" style="background: #f1f1f1">
                <span :class="(submission.status.toUpperCase() === 'DRAFT') ? 'text-gray-light' :
                              (submission.status.toUpperCase() === 'PAYMENT_FAILED') ? 'text-red' :
                              (submission.status.toUpperCase() === 'AWAITING_PAYMENT') ? 'text-purple' :
                              (submission.status.toUpperCase() === 'PENDING') ? 'text-gray' :
                              (submission.status.toUpperCase() === 'ASSIGNED')? 'text-blue':
                              (submission.status.toUpperCase() === 'APPROVED') ? 'text-green' :
                              (submission.status.toUpperCase() === 'AWAITING_NAME_CHANGE') ? 'text-orange' :
                              (submission.status.toUpperCase() === 'QUERIED') ? 'text-orange' : ''">
                    <span v-if="submission.status.toUpperCase() === 'AWAITING_PAYMENT'">
                      Confirming Payment
                    </span>
                    <span v-else-if="submission.status.toUpperCase() === 'PENDING'">
                      In Queue
                    </span>
                    <span v-else>
                      {{ submission.status.toLowerCase().replaceAll("_", " ") }}
                    </span>
                </span>
              </p>
            </td>
            <td class="t-body text-center">
              <span class="text-xs" v-if="submission.registrarName">
                {{ submission.registrarName }}
                <br v-if="submission.status.toUpperCase() === 'ASSIGNED' && submission.updatedAt">
                <span v-if="submission.status.toUpperCase() === 'ASSIGNED' && submission.updatedAt" 
                      class="text-xs text-gray-600 italic">
                  {{ calculateAssignmentDuration(submission.updatedAt) }}
                </span>
              </span>
              <span class="text-xs" v-else>Not Assigned</span>
            </td>
            <td class="t-body text-center">
              <b-row v-if="submission.status.toUpperCase() === 'AWAITING_PAYMENT'">
                <b-col>
                  <button class="w-full bg-blue text-white mx-1 px-2 py-1 rounded-sm" @click="move(submission)">
                    <i class="fa fa-reply mx-1"></i> Move to Queue
                    <b-spinner v-if="loadingMoveTask" small></b-spinner>
                  </button>
                </b-col>
                <b-col v-if="canViewActionWithPermission('can_remove_payment_in_continuous_filing')">
                  <button
                    class="bg-red text-white mx-1 px-2 py-1 rounded-sm" @click="removePayment(submission, idx)">
                    <i class="fa fa-times mx-1"></i> Remove Payment
                    <b-spinner v-if="loadingRevertTask && selectedId === idx" small></b-spinner>
                  </button>
                </b-col>
              </b-row>
              <b-row
                v-else-if="(submission.status.toUpperCase() === 'PAYMENT_FAILED' || submission.status.toUpperCase() === 'DRAFT')">
                <b-col v-if="canViewActionWithPermission('can_add_prn_in_continuous_filing') ">
                  <button
                    class="w-full text-xs bg-blue text-white px-2 py-1 rounded-sm" @click="addPRN(submission)">
                    <i class="fa fa-plus mx-1"></i> Add PRN
                  </button>
                </b-col>
                <b-col v-if="canViewActionWithPermission('can_remove_payment_in_continuous_filing')">
                  <button
                    class="bg-red text-white mx-1 px-2 py-1 rounded-sm" @click="removePayment(submission, idx)">
                    <i class="fa fa-times mx-1"></i> Remove Payment
                    <b-spinner v-if="loadingRevertTask && selectedId === idx" small></b-spinner>
                  </button>
                </b-col>
                <b-col v-if="canExemptStampDuty(submission) || hasAnyExemptionToDrop(submission)">
                  <button
                    v-if="canExemptStampDuty(submission)"
                    class="w-full text-xs bg-green text-white px-2 py-1 rounded-sm my-1"
                    @click="markStampDutyExempt(submission)">
                    <i class="fa fa-check mx-1"></i> Exempt Stamp Duty
                  </button>

                  <template v-if="hasBothExemptionsToDrop(submission)">
                    <button
                      class="w-full text-xs bg-red text-white px-2 py-1 rounded-sm my-1"
                      @click="dropStampDutyExemption(submission, 'SHARE_CAPITAL')">
                      <i class="fa fa-times mx-1"></i> Drop Share Capital Exemption
                      <b-spinner v-if="loadingDropExemption && selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_CAPITAL'" small></b-spinner>
                    </button>
                    <button
                      class="w-full text-xs bg-red text-white px-2 py-1 rounded-sm my-1"
                      @click="dropStampDutyExemption(submission, 'SHARE_TRANSFER')">
                      <i class="fa fa-times mx-1"></i> Drop Share Transfer Exemption
                      <b-spinner v-if="loadingDropExemption && selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_TRANSFER'" small></b-spinner>
                    </button>
                  </template>
                  <template v-else-if="canDropShareCapitalExemption(submission)">
                    <button
                      class="w-full text-xs bg-red text-white px-2 py-1 rounded-sm my-1"
                      @click="dropStampDutyExemption(submission, 'SHARE_CAPITAL')">
                      <i class="fa fa-times mx-1"></i> Drop Stamp Duty Exemption
                      <b-spinner v-if="loadingDropExemption && (selectedExemptionTrackingNo === submission.trackingNo || selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_CAPITAL')" small></b-spinner>
                    </button>
                  </template>
                  <template v-else-if="canDropShareTransferExemption(submission)">
                    <button
                      class="w-full text-xs bg-red text-white px-2 py-1 rounded-sm my-1"
                      @click="dropStampDutyExemption(submission, 'SHARE_TRANSFER')">
                      <i class="fa fa-times mx-1"></i> Drop Stamp Duty Exemption
                      <b-spinner v-if="loadingDropExemption && (selectedExemptionTrackingNo === submission.trackingNo || selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_TRANSFER')" small></b-spinner>
                    </button>
                  </template>
                </b-col>
              </b-row>
              <button
                v-else-if="submission.status.toUpperCase() === 'ESCALATED' && canViewActionWithPermission('can_assign_continuous_filing_application')"
                class="bg-blue text-white px-2 py-1 rounded-sm" @click="assignPrompt(submission)">
                <i class="fa fa-plus mx-1"></i>Assign
              </button>
              <button
                  v-show="!submission.parentTrackingNo"
                v-else-if="submission.status.toUpperCase() === 'PENDING' && canViewActionWithPermission('can_assign_continuous_filing_application')"
                class="bg-green text-white px-2 py-1 rounded-sm" @click="assignPrompt(submission)">
                <i class="fa fa-plus mx-1"></i>Assign
              </button>
              <button
                v-else-if="submission.status.toUpperCase() === 'ASSIGNED' && canViewActionWithPermission('can_assign_continuous_filing_application')"
                class="bg-red text-white px-2 py-1 rounded-sm" @click="assignPrompt(submission)">
                <i class="fa fa-redo-alt mx-1"></i>Assign
              </button>
              <div
                v-else-if="submission.status.toUpperCase() === 'QUERIED' && (canExemptStampDuty(submission) || hasAnyExemptionToDrop(submission))"
                class="flex flex-col gap-1 items-center">
                <button
                  v-if="canExemptStampDuty(submission)"
                  class="bg-red text-white px-2 py-1 rounded-sm text-xs"
                  @click="markStampDutyExempt(submission)">
                  <i class="fa fa-check mx-1"></i> Exempt Stamp Duty
                </button>

                <template v-if="hasBothExemptionsToDrop(submission)">
                  <button
                    class="bg-red text-white px-2 py-1 rounded-sm text-xs"
                    @click="dropStampDutyExemption(submission, 'SHARE_CAPITAL')">
                    <i class="fa fa-times mx-1"></i> Drop Share Capital Exemption
                    <b-spinner v-if="loadingDropExemption && selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_CAPITAL'" small></b-spinner>
                  </button>
                  <button
                    class="bg-red text-white px-2 py-1 rounded-sm text-xs"
                    @click="dropStampDutyExemption(submission, 'SHARE_TRANSFER')">
                    <i class="fa fa-times mx-1"></i> Drop Share Transfer Exemption
                    <b-spinner v-if="loadingDropExemption && selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_TRANSFER'" small></b-spinner>
                  </button>
                </template>
                <template v-else-if="canDropShareCapitalExemption(submission)">
                  <button
                    class="bg-red text-white px-2 py-1 rounded-sm text-xs"
                    @click="dropStampDutyExemption(submission, 'SHARE_CAPITAL')">
                    <i class="fa fa-times mx-1"></i> Drop Stamp Duty Exemption
                    <b-spinner v-if="loadingDropExemption && (selectedExemptionTrackingNo === submission.trackingNo || selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_CAPITAL')" small></b-spinner>
                  </button>
                </template>
                <template v-else-if="canDropShareTransferExemption(submission)">
                  <button
                    class="bg-red text-white px-2 py-1 rounded-sm text-xs"
                    @click="dropStampDutyExemption(submission, 'SHARE_TRANSFER')">
                    <i class="fa fa-times mx-1"></i> Drop Stamp Duty Exemption
                    <b-spinner v-if="loadingDropExemption && (selectedExemptionTrackingNo === submission.trackingNo || selectedExemptionTrackingNo === submission.trackingNo + 'SHARE_TRANSFER')" small></b-spinner>
                  </button>
                </template>
              </div>
              <button
                v-else-if="submission.status.toUpperCase() === 'APPROVED' && canViewActionWithPermission('can_rollback_continuous_filing_application')"
                class="bg-red text-white px-2 py-1 rounded-sm"
                @click="rollbackPrompt(submission)">
                <i class="fa fa-undo mx-1"></i>Rollback
              </button>
            </td>
            <td class="t-body text-center">
              <span v-if="submission.status==='APPROVED'">{{ dateTimeFormat(submission.dateOfEffect) }}</span>
              <span v-else>{{ dateTimeFormat(submission.submissionDate) }}</span>
            </td>
          </tr>
          <tr>
            <td v-if="submissions.length === 0" colspan="12" class="bg-inertgrey text-center py-1">
              <span class="text-sm">No results found</span>
            </td>
          </tr>
          <tr>
            <td v-if="hasError" colspan="12" class="bg-red text-center py-1 text-white">
              <p class="text-sm">Failed to load tasks, Try again later.</p>
              <p class="text-sm">{{ errorMessage }}</p>
              <p class="text-sm">
                <button class="bg-white text-red px-2 rounded-sm" @click="fetch">Reload
                  <b-spinner v-if="loading" small></b-spinner>
                </button>
              </p>
            </td>
          </tr>
          </tbody>
        </table>
      </b-col>
    </b-row>
    <b-row class="mx-1" v-if="pagination.totalPages > 1">
      <b-col cols="1">
        <button @click="previous" :class="(currentPage > 1) ? 'paginate-btn' :'inert-btn'">Previous</button>
      </b-col>
      <b-col cols="1">
        <b-row>
          <b-col cols="12">
            <p class="paginate-details text-center">
              <b-badge class="bg-blue text-white">
                {{ currentPage }}/{{ pagination.totalPages }}
              </b-badge>
            </p>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="1">
        <button @click="next" :class="(currentPage < pagination.totalPages) ? 'paginate-btn' :'inert-btn'">Next</button>
      </b-col>
      <b-col class="6"></b-col>
      <b-col cols="1">
        <b-form-select v-model="limit" class="w-full text-xs form-select border-0 paginate-details" v-on:change="fetch">
          <b-select-option value="12">12</b-select-option>
          <b-select-option value="20">20</b-select-option>
          <b-select-option value="50">50</b-select-option>
          <b-select-option value="100">100</b-select-option>
        </b-form-select>
      </b-col>
      <b-col cols="2">
        <b-row>
          <b-col class="mx-auto">
            <p class="paginate-details text-center text-xs font-bold">
              <span>{{ ((currentPage - 1) * limit) + 1 }}</span> -
              <span v-if="limit > submissions.length ">{{ pagination.totalElements }}</span>
              <span v-else>{{ currentPage * limit }}</span>
              of {{ pagination.totalElements }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import {mapActions, mapGetters} from "vuex";
import moment from "moment";
import Assignment from "@/views/home/commons/Assignment";
import AddPRN from "@/views/home/commons/AddPRN";
import StampDutyExemptionForm from "@/views/home/commons/StampDutyExemptionForm";
import { api } from "@/app/services/api.service";

export default {
  name: "AllContinuousFilingSubmissions",
  components: { AddPRN, Assignment, StampDutyExemptionForm },
  data() {
    return {
      loading: false,
      loadingMoveTask: false,
      loadingRevertTask: false,
      loadingRollback: false,
      loadingDropExemption: false,
      selectedExemptionTrackingNo: null,
      currentPage: 1,
      limit: 12,
      submissions: [],
      pagination: {
        totalElements: 0,
        totalPages: 0
      },
      selectedId: null,
      taskId: null,
      rows: {
        odd: "border-t border-grey hover:bg-header_grey",
        even: "border-t border-grey hover:bg-header_grey"
      },
      search: "",
      statuses: "PENDING,AWAITING_NAME_CHANGE",
      application: {},
      application_prn: null,
      selectedApplication: {},
      hasError: false,
      errorMessage: null,
      continuousFilingApplicationTypes : [
          { value: null, text: "Please select application type" },
          { value: "SHARE_CAPITAL", text: "Share Capital" },
          { value: "SHARE_TRANSFER", text: "Share Transfer" }
        ],
      startRegistrationDate:null,
      endRegistrationDate:null,
      rollbackForm: {
        trackingNo: "",
        rationale: "",
        keepDocuments: false,
      },
    };
  },
  mounted() {
      // this.statuses = "PENDING"
    this.fetch();
  },
  methods: {
    ...mapActions(["submitContinuousFilingApplicationPayment", "fetchContinuousFilingSubmissions", "redoTransaction",
      "removePaymentFromCFApplication", "submitStampDutyExemption"]),
    fetch() {
      let options = this.statuses;
      // if (this.statuses == null) {
      //   options = "PENDING,AWAITING_NAME_CHANGE"
      //     DRAFT,,ASSIGNED,QUERIED,ESCALATED,APPROVED";
      // }
      let data = {
        search: this.search,
        statuses: options,
        page: this.currentPage,
        limit: this.limit,
        startRegistrationDate: this.startRegistrationDate ? moment(this.startRegistrationDate).format('YYYY-MM-DD') : null,
        endRegistrationDate: this.endRegistrationDate ? moment(this.endRegistrationDate).format('YYYY-MM-DD') : null,

      };
      this.loading = true;
      this.fetchContinuousFilingSubmissions(data);
    },
    showDetails(trackingNumber) {
      if (trackingNumber.startsWith("800")) {
        this.warningToast("Unauthorized Action", "Go to Registry to see approved Changes");
      } else {
        this.$emit("openDetails", trackingNumber);
      }
    },

    next() {
      if (this.currentPage < this.pagination.totalPages) {
        this.currentPage++;
        this.fetch();
      }
    },
    previous() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetch();
      } else {
        this.warningToast("Observation", "No more data to show");
      }
    },
    addPRN(application) {
      this.application = application;
      this.$refs.addPRN.show();
    },
    move(application) {
      let data = {
        userId: JSON.parse(sessionStorage.getItem("current_user")).userId,
        prn: application.prn || (application.payment && application.payment.prn)
      };
      this.redoTransaction(data);
    },
    removePayment(application, idx) {
      this.selectedId = idx;
      let data = {
        trackingNo: application.trackingNo
      };
      this.loadingRevertTask = true;
      this.removePaymentFromCFApplication(data);
    },
    dateFormat(date) {
      if (date) {
        return moment(date).format("DD-MM-YYYY");
      }
      return "";
    },
    dateTimeFormat(date) {
      if (date) {
        return moment(date).format("DD-MM-YYYY h:mmA");
      }
      return "";
    },
    assignPrompt(data) {
      this.taskId = data.taskId;
      this.$refs.assignSubmissions.show();
    },
    cleanEntityType(name) {
      let n = name.replaceAll("-", " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    cleanString(name) {
      let n = name.replaceAll("_", " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    canViewActionWithPermission(permission) {
      return this.$can(permission, "general") === true;
    },
    hasShareCapitalChange(submission) {
      return Boolean(
        submission &&
        submission.filingChangeType &&
        submission.filingChangeType.includes("CHANGE_SHARE_CAPITAL")
      );
    },
    hasShareTransferChange(submission) {
      return Boolean(
        submission &&
        submission.filingChangeType &&
        submission.filingChangeType.includes("CHANGE_SHARES")
      );
    },
    isShareCapitalExempted(submission) {
      if (!this.hasShareCapitalChange(submission)) return false;
      return (
        submission.shareCapitalStampDutyExemption === "EXEMPTED" ||
        (submission.stampDutyExemption != null && submission.stampDutyExemption !== "NOT_EXEMPTED")
      );
    },
    isShareTransferExempted(submission) {
      if (!this.hasShareTransferChange(submission)) return false;
      return (
        submission.transferStampDutyExemption === "EXEMPTED" ||
        (!this.hasShareCapitalChange(submission) &&
          submission.stampDutyExemption != null &&
          submission.stampDutyExemption !== "NOT_EXEMPTED")
      );
    },
    canExemptStampDuty(submission) {
      if (!this.canViewActionWithPermission("can_mark_application_stamp_duty_exempt")) return false;
      const canExemptCapital = this.hasShareCapitalChange(submission) && !this.isShareCapitalExempted(submission);
      const canExemptTransfer = this.hasShareTransferChange(submission) && !this.isShareTransferExempted(submission);
      return canExemptCapital || canExemptTransfer;
    },
    canDropShareCapitalExemption(submission) {
      return (
        this.canViewActionWithPermission("can_mark_application_stamp_duty_exempt") &&
        this.isShareCapitalExempted(submission)
      );
    },
    canDropShareTransferExemption(submission) {
      return (
        this.canViewActionWithPermission("can_mark_application_stamp_duty_exempt") &&
        this.isShareTransferExempted(submission)
      );
    },
    hasAnyExemptionToDrop(submission) {
      return this.canDropShareCapitalExemption(submission) || this.canDropShareTransferExemption(submission);
    },
    hasBothExemptionsToDrop(submission) {
      return this.canDropShareCapitalExemption(submission) && this.canDropShareTransferExemption(submission);
    },
    markStampDutyExempt(application) {
      this.selectedApplication = application;
      this.continuousFilingApplicationTypes = [];
      if (this.hasShareCapitalChange(application) && !this.isShareCapitalExempted(application)) {
        this.continuousFilingApplicationTypes.push({ value: "SHARE_CAPITAL", text: "Share Capital" });
      }
      if (this.hasShareTransferChange(application) && !this.isShareTransferExempted(application)) {
        this.continuousFilingApplicationTypes.push({ value: "SHARE_TRANSFER", text: "Share Transfer" });
      }
      this.$refs.exemptionModal.show();
    },
    handleExemptionSubmitted() {
      this.$refs.exemptionModal.hide();
      this.fetch();
    },
    dropStampDutyExemption(submission, specificType = null) {
      let type = specificType;
      if (!type) {
        if (this.isShareCapitalExempted(submission) && !this.isShareTransferExempted(submission)) {
          type = "SHARE_CAPITAL";
        } else if (this.isShareTransferExempted(submission) && !this.isShareCapitalExempted(submission)) {
          type = "SHARE_TRANSFER";
        } else if (this.isShareCapitalExempted(submission)) {
          type = "SHARE_CAPITAL";
        } else {
          type = "SHARE_TRANSFER";
        }
      }

      const typeLabel = type === "SHARE_CAPITAL" ? "Share Capital" : "Share Transfer";
      const message = `Are you sure you want to drop the ${typeLabel} stamp duty exemption for ${submission.trackingNo}?`;

      this.showConfirmDialog(message).then(value => {
        if (value) {
          this.loadingDropExemption = true;
          this.selectedExemptionTrackingNo = submission.trackingNo + type;
          api().post(`/incorporation/backoffice-edits/remove-exemption-from-stamp-duty/${submission.trackingNo}/${type}`)
            .then(() => {
              this.loadingDropExemption = false;
              this.selectedExemptionTrackingNo = null;
              this.successToast("Success", `${typeLabel} stamp duty exemption removed successfully`);
              this.fetch();
            })
            .catch(error => {
              this.loadingDropExemption = false;
              this.selectedExemptionTrackingNo = null;
              this.errorToast("Error", error.response?.data?.message || "Failed to remove stamp duty exemption");
            });
        }
      });
    },
    rollbackPrompt(submission) {
      this.rollbackForm.trackingNo = submission.trackingNo
      this.rollbackForm.rationale = ""
      this.rollbackForm.keepDocuments = false
      this.$refs.rollbackModal.show()
    },
    submitRollback() {
      this.loadingRollback = true
      api().post("/incorporation/companies/continuous-filing/rollback-resolution", this.rollbackForm)
        .then(() => {
          this.loadingRollback = false
          this.$refs.rollbackModal.hide()
          this.successToast("Success", "Application rolled back successfully")
          this.fetch()
        })
        .catch(error => {
          this.loadingRollback = false
          const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to rollback application"
          this.errorToast("Error", message)
        })
    },
  },
  computed: {
    ...mapGetters(["getContinuousFilingSubmissions", "getMoveTask",
      "getContinuousFilingTask", "getContinuousFilingTaskError",
      "getMoveTaskError", "getContinuousFilingError",
      "getIncorporationPaymentRemoved", "getIncorporationPaymentRemovedError"])
  },
  watch: {
    getContinuousFilingTask() {
      this.loading = false;
      this.application_prn = null;
      this.successToast("Success", "PRN Attachment Successful");
      this.$refs.addPRN.hide();
      this.fetch();
    },
    getContinuousFilingTaskError(response) {
      this.loading = false;
      this.errorToast("Error", response.data.message);
    },
    getContinuousFilingSubmissions(response) {
      this.loading = false;
      this.submissions = response.data.content;
      this.pagination = {
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements
      };
    },
    getContinuousFilingError(data) {
      this.loading = false;
      this.hasError = true;
      this.errorMessage = data.message;
      this.errorToast("Error", data.message);
    },
    getNetworkStatus(message) {
      this.loading = false;
      this.hasError = true;
      this.errorMessage = message;
      this.errorToast("Network Error", message);
    },
    getMoveTask() {
      this.loadingMoveTask = false;
      this.successToast("Request Success", "Transaction retried");
      this.fetch();
    },
    getMoveTaskError(data) {
      this.loadingMoveTask = false;
      this.errorToast("Error", data.message);
    },
    getIncorporationPaymentRemoved() {
      this.loadingRevertTask = false;
      this.successToast("Success", "Payment details removed and status reverted");
      this.fetch();
    },
    getIncorporationPaymentRemovedError(data) {
      this.loadingRevertTask = false;
      this.errorToast("Success", data.message);
    }
  }
};
</script>

<style scoped>
@import "../../../../../assets/styles/table.css";

.verified {
  @apply text-xs font-bold;
  color: green;
  font-style: italic;
}

.unverified {
  @apply text-xs font-bold;
  color: #b80404;
  font-style: italic;
}

.assign-btn {
  @apply px-3 py-1 bg-white border-2 font-bold border-green text-green text-xs;
}

.assign-btn:hover {
  @apply bg-green text-white;
}

.paginate-details {
  @apply px-3 py-2 bg-white my-2 text-sm text-gray;
}

.paginate-btn {
  @apply px-3 py-2 bg-white border border-white my-2 text-sm text-blue w-full;
}

.paginate-btn:hover {
  @apply border border-blue text-blue rounded-sm;
}

.inert-btn {
  @apply px-3 py-2 bg-white my-2 text-sm text-grey w-full;
}

.rollback-keep-docs >>> .custom-control-label {
  padding-left: 0.5rem;
}
</style>